// src/pages/orders/adminTaxonomyPage.js — Categories & Collections (catalog CMS).
//
// The last catalog concept that still required opening Saleor. Both live on one screen
// because there is very little of either — Saleor held ONE category ("Hardware",
// containing every product) and ONE collection ("Featured Products", 4 of 6) — and two
// near-empty pages would be worse than one honest one.
//
// THE TWO ARE NOT SYMMETRICAL, and the UI says so rather than pretending otherwise:
//
//   A category is WHERE A PRODUCT LIVES. One per product, edited from the product page,
//   so this screen manages the category list itself and shows how many products are in
//   each — it does not assign them.
//
//   A collection is A SET A PRODUCT APPEARS IN. Many per product, curated, and its
//   ORDER IS CONTENT, so membership and ordering are edited here.
//
// Reordering is up/down buttons rather than drag-and-drop. Four items do not need a
// drag library, and buttons work with a keyboard.
import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, CardHeader } from "../../components/card/card.jsx";
import {
  fetchTaxonomy, fetchCollection, fetchProductPool,
  saveCategory, deleteCategory, saveCollection, deleteCollection, setCollectionProducts,
} from "../../actions/taxonomy_actions";

function Feedback({ result }) {
  if (!result) return null;
  return <div className={`small mt-2 ${result.ok ? "text-success" : "text-danger"}`}>{result.ok ? result.text || "Saved." : result.message}</div>;
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div className="mb-2">
      <label className="form-label small text-inverse text-opacity-50 mb-1">{label}</label>
      <input className="form-control form-control-sm" value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// slug is derived from the name on CREATE only. Editing a slug afterwards changes a
// public URL, so it stays a deliberate act rather than something that quietly follows a
// rename.
const slugify = (s) => String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// ---------------------------------------------------------------- categories

function CategoryEditor({ categories, onDone, editing, onCancel }) {
  const [name, setName] = useState(editing ? editing.name : "");
  const [slug, setSlug] = useState(editing ? editing.slug : "");
  const [parent, setParent] = useState(editing ? editing.parent || "" : "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const setNameAndSlug = (v) => { setName(v); if (!editing) setSlug(slugify(v)); };

  const save = async () => {
    setBusy(true); setResult(null);
    const r = await saveCategory({ id: editing ? editing.id : undefined, slug, name, parent: parent || undefined });
    setBusy(false); setResult(r);
    if (r.ok) onDone();
  };

  return (
    <div className="border rounded p-2 mb-2">
      <Field label="Name" value={name} onChange={setNameAndSlug} />
      <Field label="Slug" value={slug} onChange={setSlug} placeholder="lower-case-with-hyphens" />
      <label className="form-label small text-inverse text-opacity-50 mb-1">Parent</label>
      <select className="form-select form-select-sm mb-2" value={parent} onChange={(e) => setParent(e.target.value)}>
        <option value="">— none (top level) —</option>
        {categories.filter((c) => !editing || c.id !== editing.id).map((c) => (
          <option key={c.id} value={c.slug}>{"— ".repeat(c.level)}{c.name}</option>
        ))}
      </select>
      <button className="btn btn-sm btn-theme me-2" disabled={busy || !name || !slug} onClick={save}>
        {busy ? "Saving…" : editing ? "Save category" : "Create category"}
      </button>
      <button className="btn btn-sm btn-outline-theme" disabled={busy} onClick={onCancel}>Cancel</button>
      <Feedback result={result} />
    </div>
  );
}

function Categories({ categories, reload }) {
  const [editing, setEditing] = useState(null);   // category object, or "new"
  const [result, setResult] = useState(null);

  const remove = async (c) => {
    // Naming the consequence, because it is not obvious: the products survive, they
    // just stop being in a category.
    const msg = c.productCount
      ? `Delete "${c.name}"? The ${c.productCount} product${c.productCount === 1 ? "" : "s"} in it are kept, but will have no category.`
      : `Delete "${c.name}"?`;
    if (!window.confirm(msg)) return;
    const r = await deleteCategory(c.id);
    setResult(r.ok
      ? { ok: true, text: `Deleted. ${r.data.orphanedProducts || 0} product${r.data.orphanedProducts === 1 ? "" : "s"} now have no category.` }
      : r);
    if (r.ok) reload();
  };

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex align-items-center">
        Categories
        <button className="ms-auto btn btn-sm btn-outline-theme" onClick={() => setEditing("new")}>+ Category</button>
      </CardHeader>
      <CardBody>
        <div className="small text-inverse text-opacity-50 mb-2">
          Where a product lives — one each. Assign a product to a category from its own page.
        </div>
        {editing ? (
          <CategoryEditor categories={categories} editing={editing === "new" ? null : editing}
            onDone={() => { setEditing(null); reload(); }} onCancel={() => setEditing(null)} />
        ) : null}
        {categories.length === 0 && !editing ? (
          <div className="text-inverse text-opacity-50 small">No categories yet.</div>
        ) : null}
        {categories.map((c) => (
          <div key={c.id} className="d-flex align-items-center py-1 border-bottom border-secondary border-opacity-10">
            <div style={{ paddingLeft: c.level * 16 }}>
              <div>{c.name}</div>
              <div className="small text-inverse text-opacity-50">/{c.slug} · {c.productCount} product{c.productCount === 1 ? "" : "s"}</div>
            </div>
            <div className="ms-auto">
              <button className="btn btn-link btn-sm p-0 me-3 text-inverse text-opacity-50" onClick={() => setEditing(c)}>Edit</button>
              <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => remove(c)}>Delete</button>
            </div>
          </div>
        ))}
        <Feedback result={result} />
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------- collections

function MembershipEditor({ slug, pool, onClose }) {
  const [members, setMembers] = useState(null);   // [{id, slug, name}] in curated order
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [add, setAdd] = useState("");

  const load = useCallback(async () => {
    const r = await fetchCollection(slug);
    // `products` is the PUBLISHED set the storefront would show. A member that is
    // unpublished is therefore invisible here — surfaced in the note below rather than
    // silently dropped, because otherwise saving would delete it.
    setMembers(r.collection ? r.collection.products.map((p) => ({ id: p.id, slug: p.slug, name: p.name })) : []);
    setResult(r.collection && r.collection.productCount !== (r.collection.products || []).length
      ? { ok: false, message: `${r.collection.productCount - r.collection.products.length} member(s) are unpublished and not shown. Saving now would remove them.` }
      : null);
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (!members) return <div className="small text-inverse text-opacity-50">Loading members…</div>;

  const move = (i, d) => {
    const next = [...members];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setMembers(next);
  };

  const save = async () => {
    setBusy(true);
    // The engine REPLACES membership, so this must be the whole list in final order.
    const r = await setCollectionProducts(
      (await fetchCollection(slug)).collection.id,
      members.map((m) => Number(m.id)));
    setBusy(false);
    setResult(r.ok ? { ok: true, text: "Members saved." } : r);
    if (r.ok) load();
  };

  const available = pool.filter((p) => !members.some((m) => String(m.id) === String(p.id)));

  return (
    <div className="border rounded p-2 mt-2">
      <div className="small text-inverse text-opacity-50 mb-2">
        Order is content — this is the order a storefront would render them in.
      </div>
      {members.length === 0 ? <div className="small text-inverse text-opacity-50 mb-2">No products in this collection.</div> : null}
      {members.map((m, i) => (
        <div key={m.id} className="d-flex align-items-center py-1">
          <span className="text-inverse text-opacity-25 me-2 small" style={{ width: 20 }}>{i + 1}</span>
          <div>{m.name}<span className="small text-inverse text-opacity-50"> /{m.slug}</span></div>
          <div className="ms-auto">
            <button className="btn btn-link btn-sm p-0 me-2 text-inverse text-opacity-50" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button className="btn btn-link btn-sm p-0 me-3 text-inverse text-opacity-50" disabled={i === members.length - 1} onClick={() => move(i, 1)}>↓</button>
            <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => setMembers(members.filter((x) => x.id !== m.id))}>remove</button>
          </div>
        </div>
      ))}
      <div className="d-flex align-items-center gap-2 mt-2">
        <select className="form-select form-select-sm" value={add} onChange={(e) => setAdd(e.target.value)}>
          <option value="">Add a product…</option>
          {available.map((p) => <option key={p.id} value={p.id}>{p.name}{p.isPublished ? "" : " (unpublished)"}</option>)}
        </select>
        <button className="btn btn-sm btn-outline-theme" disabled={!add} onClick={() => {
          const p = pool.find((x) => String(x.id) === String(add));
          if (p) setMembers([...members, { id: p.id, slug: p.slug, name: p.name }]);
          setAdd("");
        }}>Add</button>
      </div>
      <div className="mt-2">
        <button className="btn btn-sm btn-theme me-2" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save members"}</button>
        <button className="btn btn-sm btn-outline-theme" onClick={onClose}>Done</button>
      </div>
      <Feedback result={result} />
    </div>
  );
}

function CollectionEditor({ editing, onDone, onCancel }) {
  const [name, setName] = useState(editing ? editing.name : "");
  const [slug, setSlug] = useState(editing ? editing.slug : "");
  const [published, setPublished] = useState(editing ? editing.isPublished : true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const save = async () => {
    setBusy(true); setResult(null);
    const r = await saveCollection({ id: editing ? editing.id : undefined, slug, name, isPublished: published });
    setBusy(false); setResult(r);
    if (r.ok) onDone();
  };

  return (
    <div className="border rounded p-2 mb-2">
      <Field label="Name" value={name} onChange={(v) => { setName(v); if (!editing) setSlug(slugify(v)); }} />
      <Field label="Slug" value={slug} onChange={setSlug} placeholder="lower-case-with-hyphens" />
      <div className="form-check mb-2">
        <input className="form-check-input" type="checkbox" id="col-pub" checked={published} onChange={(e) => setPublished(e.target.checked)} />
        <label className="form-check-label small" htmlFor="col-pub">Published — visible to the storefront</label>
      </div>
      <button className="btn btn-sm btn-theme me-2" disabled={busy || !name || !slug} onClick={save}>
        {busy ? "Saving…" : editing ? "Save collection" : "Create collection"}
      </button>
      <button className="btn btn-sm btn-outline-theme" disabled={busy} onClick={onCancel}>Cancel</button>
      <Feedback result={result} />
    </div>
  );
}

function Collections({ collections, pool, reload }) {
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);   // slug
  const [result, setResult] = useState(null);

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? The ${c.productCount} product${c.productCount === 1 ? "" : "s"} in it are not affected.`)) return;
    const r = await deleteCollection(c.id);
    setResult(r.ok ? { ok: true, text: "Deleted." } : r);
    if (r.ok) reload();
  };

  return (
    <Card>
      <CardHeader className="d-flex align-items-center">
        Collections
        <button className="ms-auto btn btn-sm btn-outline-theme" onClick={() => setEditing("new")}>+ Collection</button>
      </CardHeader>
      <CardBody>
        <div className="small text-inverse text-opacity-50 mb-2">
          Curated sets a product appears in — many per product, and the order matters.
        </div>
        {editing ? (
          <CollectionEditor editing={editing === "new" ? null : editing}
            onDone={() => { setEditing(null); reload(); }} onCancel={() => setEditing(null)} />
        ) : null}
        {collections.length === 0 && !editing ? (
          <div className="text-inverse text-opacity-50 small">No collections yet.</div>
        ) : null}
        {collections.map((c) => (
          <div key={c.id} className="py-2 border-bottom border-secondary border-opacity-10">
            <div className="d-flex align-items-center">
              <div>
                <div>
                  {c.name}
                  {c.isPublished ? null : <span className="badge bg-secondary ms-2">Draft</span>}
                </div>
                <div className="small text-inverse text-opacity-50">/{c.slug} · {c.productCount} product{c.productCount === 1 ? "" : "s"}</div>
              </div>
              <div className="ms-auto">
                <button className="btn btn-link btn-sm p-0 me-3 text-inverse text-opacity-50"
                  onClick={() => setManaging(managing === c.slug ? null : c.slug)}>
                  {managing === c.slug ? "Close" : "Products"}
                </button>
                <button className="btn btn-link btn-sm p-0 me-3 text-inverse text-opacity-50" onClick={() => setEditing(c)}>Edit</button>
                <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => remove(c)}>Delete</button>
              </div>
            </div>
            {managing === c.slug ? <MembershipEditor slug={c.slug} pool={pool} onClose={() => setManaging(null)} /> : null}
          </div>
        ))}
        <Feedback result={result} />
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------- page

export default function AdminTaxonomy() {
  const [state, setState] = useState({ loading: true, categories: [], collections: [], error: null });
  const [pool, setPool] = useState([]);

  const reload = useCallback(async () => {
    const [t, p] = await Promise.all([fetchTaxonomy(), fetchProductPool()]);
    setState({ loading: false, categories: t.categories, collections: t.collections, error: t.ok ? null : t.message });
    setPool(p.products);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (state.loading) return <div className="text-center py-5 text-inverse text-opacity-50">Loading…</div>;

  return (
    <div>
      <h1 className="page-header">Categories &amp; Collections</h1>
      {state.error ? <div className="alert alert-danger">Could not load: {state.error}</div> : null}
      <div className="row">
        <div className="col-xl-6"><Categories categories={state.categories} reload={reload} /></div>
        <div className="col-xl-6"><Collections collections={state.collections} pool={pool} reload={reload} /></div>
      </div>
    </div>
  );
}
